/**
 * Pre-join device check — camera preview, mic meter, speaker test, device picks.
 * Runs before LiveKitRoom connects.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Camera,
  CameraOff,
  Mic,
  MicOff,
  Volume2,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { formatMediaError } from "./mediaDevices";

export type DeviceCheckSelection = {
  audioEnabled: boolean;
  videoEnabled: boolean;
  audioDeviceId?: string;
  videoDeviceId?: string;
  audioOutputDeviceId?: string;
};

type Props = {
  displayName: string;
  sessionTitle?: string;
  role: "trainer" | "student";
  /** Trainer defaults mic/cam on; student defaults off after check */
  defaultAudioEnabled?: boolean;
  defaultVideoEnabled?: boolean;
  onJoin: (selection: DeviceCheckSelection) => void;
  onCancel?: () => void;
};

type DeviceLists = {
  audioInputs: MediaDeviceInfo[];
  videoInputs: MediaDeviceInfo[];
  audioOutputs: MediaDeviceInfo[];
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "#0f1117",
  color: "#e2e8f0",
  fontSize: 13,
};

async function enumerateAllDevices(): Promise<DeviceLists> {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return { audioInputs: [], videoInputs: [], audioOutputs: [] };
  }
  const devices = await navigator.mediaDevices.enumerateDevices();
  const real = (d: MediaDeviceInfo) =>
    Boolean(d.deviceId) && d.deviceId !== "default" && d.deviceId !== "communications";
  return {
    audioInputs: devices.filter((d) => d.kind === "audioinput" && real(d)),
    videoInputs: devices.filter((d) => d.kind === "videoinput" && real(d)),
    audioOutputs: devices.filter((d) => d.kind === "audiooutput" && real(d)),
  };
}

function playSpeakerTone(deviceId?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const Ctx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.value = 0.08;
      osc.connect(gain);

      const finish = () => {
        try {
          osc.stop();
        } catch {
          /* ignore */
        }
        void ctx.close();
        resolve();
      };

      const start = async () => {
        await ctx.resume();
        const dest = ctx.createMediaStreamDestination();
        gain.connect(dest);
        gain.connect(ctx.destination);

        const audio = new Audio();
        audio.srcObject = dest.stream;
        // setSinkId is Chromium — optional speaker routing
        const maybeSetSink = (
          audio as HTMLAudioElement & {
            setSinkId?: (id: string) => Promise<void>;
          }
        ).setSinkId;
        if (deviceId && maybeSetSink) {
          try {
            await maybeSetSink.call(audio, deviceId);
          } catch {
            /* fall back to default output */
          }
        }
        await audio.play();
        osc.start();
        gain.gain.setValueAtTime(0.08, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
        window.setTimeout(finish, 500);
      };

      void start().catch(reject);
    } catch (err) {
      reject(err);
    }
  });
}

export function DeviceCheckScreen({
  displayName,
  sessionTitle,
  role,
  defaultAudioEnabled = role === "trainer",
  defaultVideoEnabled = role === "trainer",
  onJoin,
  onCancel,
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);

  const [devices, setDevices] = useState<DeviceLists>({
    audioInputs: [],
    videoInputs: [],
    audioOutputs: [],
  });
  const [audioDeviceId, setAudioDeviceId] = useState("");
  const [videoDeviceId, setVideoDeviceId] = useState("");
  const [outputDeviceId, setOutputDeviceId] = useState("");
  const [audioEnabled, setAudioEnabled] = useState(defaultAudioEnabled);
  const [videoEnabled, setVideoEnabled] = useState(defaultVideoEnabled);
  const [micLevel, setMicLevel] = useState(0);
  const [loading, setLoading] = useState(true);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [speakerMsg, setSpeakerMsg] = useState<string | null>(null);
  const [busySpeaker, setBusySpeaker] = useState(false);

  const stopPreview = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    analyserRef.current = null;
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => undefined);
      audioCtxRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startMicMeter = useCallback((stream: MediaStream) => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const audioTracks = stream.getAudioTracks();
    if (!audioTracks.length) {
      setMicLevel(0);
      return;
    }
    try {
      const Ctx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) sum += data[i];
        const avg = sum / data.length / 255;
        setMicLevel(Math.min(1, avg * 2.2));
        rafRef.current = requestAnimationFrame(tick);
      };
      void ctx.resume().then(() => {
        rafRef.current = requestAnimationFrame(tick);
      });
    } catch {
      setMicLevel(0);
    }
  }, []);

  const openPreview = useCallback(
    async (opts: {
      audio: boolean;
      video: boolean;
      audioId?: string;
      videoId?: string;
    }) => {
      // ---------------------------------------------------------------
      // step 1: Stop previous preview tracks
      // ---------------------------------------------------------------
      stopPreview();
      setPermissionError(null);

      if (!opts.audio && !opts.video) {
        setLoading(false);
        return;
      }

      try {
        // ---------------------------------------------------------------
        // step 2: getUserMedia for preview (permission + live feeds)
        // ---------------------------------------------------------------
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: opts.audio
            ? {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
                ...(opts.audioId ? { deviceId: { ideal: opts.audioId } } : {}),
              }
            : false,
          video: opts.video
            ? {
                facingMode: "user",
                ...(opts.videoId ? { deviceId: { ideal: opts.videoId } } : {}),
              }
            : false,
        });
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = opts.video ? stream : null;
          if (opts.video) {
            await videoRef.current.play().catch(() => undefined);
          }
        }

        if (opts.audio) startMicMeter(stream);

        // ---------------------------------------------------------------
        // step 3: Re-enumerate so labels are populated after permission
        // ---------------------------------------------------------------
        const listed = await enumerateAllDevices();
        setDevices(listed);
        setAudioDeviceId((prev) => {
          if (prev && listed.audioInputs.some((d) => d.deviceId === prev)) {
            return prev;
          }
          const fromTrack = stream.getAudioTracks()[0]?.getSettings().deviceId;
          return (
            fromTrack ||
            listed.audioInputs[0]?.deviceId ||
            ""
          );
        });
        setVideoDeviceId((prev) => {
          if (prev && listed.videoInputs.some((d) => d.deviceId === prev)) {
            return prev;
          }
          const fromTrack = stream.getVideoTracks()[0]?.getSettings().deviceId;
          return (
            fromTrack ||
            listed.videoInputs[0]?.deviceId ||
            ""
          );
        });
        setOutputDeviceId((prev) => {
          if (prev && listed.audioOutputs.some((d) => d.deviceId === prev)) {
            return prev;
          }
          return listed.audioOutputs[0]?.deviceId || "";
        });
      } catch (err) {
        const kind = opts.video ? "video" : "audio";
        setPermissionError(formatMediaError(err, kind));
      } finally {
        setLoading(false);
      }
    },
    [startMicMeter, stopPreview],
  );

  useEffect(() => {
    // ---------------------------------------------------------------
    // step 1: Initial preview with role defaults
    // ---------------------------------------------------------------
    setLoading(true);
    void openPreview({
      audio: true,
      video: true,
      audioId: audioDeviceId || undefined,
      videoId: videoDeviceId || undefined,
    });
    return () => stopPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshWithSelection = (next: {
    audioEnabled?: boolean;
    videoEnabled?: boolean;
    audioDeviceId?: string;
    videoDeviceId?: string;
  }) => {
    const audioOn = next.audioEnabled ?? audioEnabled;
    const videoOn = next.videoEnabled ?? videoEnabled;
    const aId = next.audioDeviceId ?? audioDeviceId;
    const vId = next.videoDeviceId ?? videoDeviceId;
    setLoading(true);
    void openPreview({
      audio: audioOn || true, // always keep mic for meter when checking
      video: videoOn,
      audioId: aId || undefined,
      videoId: vId || undefined,
    });
  };

  const handleJoin = () => {
    // ---------------------------------------------------------------
    // step 1: Stop preview so devices free for LiveKit publish
    // ---------------------------------------------------------------
    stopPreview();
    onJoin({
      audioEnabled,
      videoEnabled,
      audioDeviceId: audioDeviceId || undefined,
      videoDeviceId: videoDeviceId || undefined,
      audioOutputDeviceId: outputDeviceId || undefined,
    });
  };

  const handleSpeakerTest = async () => {
    setBusySpeaker(true);
    setSpeakerMsg(null);
    try {
      await playSpeakerTone(outputDeviceId || undefined);
      setSpeakerMsg("Did you hear the beep? Speakers look OK.");
    } catch {
      setSpeakerMsg(
        "Could not play test sound. Check speaker / headphone output.",
      );
    } finally {
      setBusySpeaker(false);
    }
  };

  return (
    <div
      style={{
        height: "100dvh",
        width: "100%",
        background: "#0f1117",
        color: "#e2e8f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 880,
          display: "flex",
          flexWrap: "wrap",
          gap: 20,
          alignItems: "stretch",
        }}
      >
        <div
          style={{
            flex: "1 1 360px",
            borderRadius: 16,
            background: "#1a1d26",
            border: "1px solid rgba(255,255,255,0.08)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 320,
          }}
        >
          <div
            style={{
              flex: 1,
              position: "relative",
              background: "#0b0d12",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {videoEnabled ? (
              <video
                ref={videoRef}
                muted
                playsInline
                autoPlay
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  minHeight: 320,
                }}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  color: "#64748b",
                  padding: 24,
                }}
              >
                <CameraOff size={40} />
                <span style={{ fontSize: 13 }}>Camera is off</span>
              </div>
            )}
            {loading ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(15,17,23,0.55)",
                  gap: 8,
                  color: "#e2e8f0",
                  fontSize: 13,
                }}
              >
                <Loader2 size={18} className="spin" />
                Checking devices…
              </div>
            ) : null}
            <div
              style={{
                position: "absolute",
                left: 12,
                bottom: 12,
                padding: "6px 10px",
                borderRadius: 8,
                background: "rgba(0,0,0,0.55)",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {displayName || "You"}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              padding: 12,
              borderTop: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <button
              type="button"
              onClick={() => {
                const next = !audioEnabled;
                setAudioEnabled(next);
                // Keep preview mic for meter even when joining muted
              }}
              style={{
                flex: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.1)",
                background: audioEnabled
                  ? "rgba(59,130,246,0.2)"
                  : "rgba(248,113,113,0.15)",
                color: audioEnabled ? "#93c5fd" : "#fca5a5",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {audioEnabled ? <Mic size={16} /> : <MicOff size={16} />}
              {audioEnabled ? "Mic on" : "Mic off"}
            </button>
            <button
              type="button"
              onClick={() => {
                const next = !videoEnabled;
                setVideoEnabled(next);
                refreshWithSelection({ videoEnabled: next });
              }}
              style={{
                flex: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.1)",
                background: videoEnabled
                  ? "rgba(59,130,246,0.2)"
                  : "rgba(248,113,113,0.15)",
                color: videoEnabled ? "#93c5fd" : "#fca5a5",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {videoEnabled ? <Camera size={16} /> : <CameraOff size={16} />}
              {videoEnabled ? "Camera on" : "Camera off"}
            </button>
          </div>
        </div>

        <div
          style={{
            flex: "1 1 280px",
            borderRadius: 16,
            background: "#141820",
            border: "1px solid rgba(255,255,255,0.08)",
            padding: 20,
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              Check your devices
            </h1>
            <p
              style={{
                margin: "6px 0 0",
                fontSize: 13,
                color: "#94a3b8",
                lineHeight: 1.45,
              }}
            >
              {sessionTitle
                ? `Confirm mic, speakers, and camera before joining “${sessionTitle}”.`
                : "Confirm mic, speakers, and camera before joining the live session."}
            </p>
          </div>

          {permissionError ? (
            <div
              style={{
                display: "flex",
                gap: 8,
                padding: 10,
                borderRadius: 10,
                background: "rgba(248,113,113,0.12)",
                border: "1px solid rgba(248,113,113,0.35)",
                color: "#fca5a5",
                fontSize: 12,
                lineHeight: 1.4,
              }}
            >
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{permissionError}</span>
            </div>
          ) : null}

          <label style={{ fontSize: 12, color: "#94a3b8" }}>
            Microphone
            <select
              style={{ ...selectStyle, marginTop: 6 }}
              value={audioDeviceId}
              onChange={(e) => {
                const id = e.target.value;
                setAudioDeviceId(id);
                refreshWithSelection({ audioDeviceId: id });
              }}
            >
              {devices.audioInputs.length === 0 ? (
                <option value="">No microphone found</option>
              ) : (
                devices.audioInputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || "Microphone"}
                  </option>
                ))
              )}
            </select>
          </label>

          <div>
            <div
              style={{
                fontSize: 12,
                color: "#94a3b8",
                marginBottom: 6,
              }}
            >
              Mic level — speak to test
            </div>
            <div
              style={{
                height: 8,
                borderRadius: 99,
                background: "rgba(255,255,255,0.08)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${Math.round(micLevel * 100)}%`,
                  background:
                    micLevel > 0.55
                      ? "#22c55e"
                      : micLevel > 0.15
                        ? "#3b82f6"
                        : "#64748b",
                  transition: "width 80ms linear",
                }}
              />
            </div>
          </div>

          <label style={{ fontSize: 12, color: "#94a3b8" }}>
            Camera
            <select
              style={{ ...selectStyle, marginTop: 6 }}
              value={videoDeviceId}
              onChange={(e) => {
                const id = e.target.value;
                setVideoDeviceId(id);
                refreshWithSelection({ videoDeviceId: id });
              }}
              disabled={!videoEnabled}
            >
              {devices.videoInputs.length === 0 ? (
                <option value="">No camera found</option>
              ) : (
                devices.videoInputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || "Camera"}
                  </option>
                ))
              )}
            </select>
          </label>

          <label style={{ fontSize: 12, color: "#94a3b8" }}>
            Speakers
            <select
              style={{ ...selectStyle, marginTop: 6 }}
              value={outputDeviceId}
              onChange={(e) => setOutputDeviceId(e.target.value)}
            >
              {devices.audioOutputs.length === 0 ? (
                <option value="">System default</option>
              ) : (
                devices.audioOutputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || "Speakers"}
                  </option>
                ))
              )}
            </select>
          </label>

          <button
            type="button"
            onClick={() => void handleSpeakerTest()}
            disabled={busySpeaker}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.04)",
              color: "#e2e8f0",
              cursor: busySpeaker ? "wait" : "pointer",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            <Volume2 size={16} />
            {busySpeaker ? "Playing…" : "Test speakers"}
          </button>
          {speakerMsg ? (
            <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
              {speakerMsg}
            </p>
          ) : null}

          <div style={{ flex: 1 }} />

          <div style={{ display: "flex", gap: 10 }}>
            {onCancel ? (
              <button
                type="button"
                onClick={onCancel}
                style={{
                  flex: 1,
                  padding: "12px 14px",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "transparent",
                  color: "#94a3b8",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleJoin}
              disabled={loading}
              style={{
                flex: 1.4,
                padding: "12px 14px",
                borderRadius: 10,
                border: "none",
                background: loading ? "#334155" : "#2563eb",
                color: "#fff",
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              {permissionError ? "Join anyway" : "Join session"}
            </button>
          </div>
          {permissionError ? (
            <button
              type="button"
              onClick={() => {
                setLoading(true);
                void openPreview({
                  audio: true,
                  video: true,
                  audioId: audioDeviceId || undefined,
                  videoId: videoDeviceId || undefined,
                });
              }}
              style={{
                border: "none",
                background: "transparent",
                color: "#60a5fa",
                cursor: "pointer",
                fontSize: 12,
                padding: 0,
                textAlign: "left",
              }}
            >
              Retry permissions
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default DeviceCheckScreen;
